import React from 'react';

import Callout from '~components/Callout';
import SectionCard from '~components/SectionCard';
import FigureTileGroup from '~components/visualisation/figure-tile/FigureTileGroup';
import Pill from '~components/visualisation/pill/Pill';
import HcpUnspentFunds from '~support-at-home/components/common/HcpUnspentFunds';
import SectionInfo from '~support-at-home/components/common/SectionInfo';

import { Tracker, TrackerHcpUnspentFunds } from '~support-at-home/models';

import { TRACKER_FIGURE_COLOURS } from './trackerPalette';
import { TRACKER_TEXT } from './trackerText';
import { pluralise, TrackerSummaryView } from './transforms';

interface ComponentProps {
  title: string;
  description: string;
  /** The budget's dates. */
  subDescription?: string;
  /** The client package's funding type name, shown after the dates. Omitted when there is none. */
  fundingType?: string | null;
  status: Tracker['status'];
  /** The client's Commonwealth unspent funds, for reference only; nothing shows when null. */
  hcpUnspentFunds?: TrackerHcpUnspentFunds | null;
  summary: TrackerSummaryView;
}

const capitalise = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

const Summary: React.FC<ComponentProps> = ({
  title,
  description,
  subDescription,
  fundingType,
  status,
  hcpUnspentFunds,
  summary,
}) => {
  const { banner, allocation, utilisation, serviceCount } = summary;

  const metaParts = [
    subDescription ? { key: 'dates', text: subDescription } : null,
    fundingType ? { key: 'funding-type', text: fundingType } : null,
    { key: 'services', text: pluralise(serviceCount, 'service') },
  ].filter((part): part is { key: string; text: string } => part !== null);

  return (
    <SectionCard
      title={title}
      description={description}
      className="budget-summary-card"
      titleAddon={<SectionInfo id="summary-section" label={title} content={TRACKER_TEXT.summary.section} />}
      headerRight={
        <div className="budget-summary-header-right">
          <HcpUnspentFunds id="tracker-hcp" funds={hcpUnspentFunds} dataCy="budget-summary-hcp" />
          <Pill text={capitalise(status)} color={TRACKER_FIGURE_COLOURS.statusPill} className="budget-summary-status" />
        </div>
      }
    >
      <div className="budget-summary-details">
        <p>
          {metaParts.map(({ key, text }, index) => (
            <React.Fragment key={key}>
              {index > 0 && ' · '}
              <span data-cy={`budget-summary-${key}`}>{text}</span>
            </React.Fragment>
          ))}
        </p>
      </div>
      {banner && (
        <Callout tone={banner.tone} title={banner.title} lines={banner.lines} dataCy="budget-summary-banner" />
      )}
      <div className="tracker-summary-groups">
        <FigureTileGroup
          title={TRACKER_TEXT.allocation.title}
          subtitle={TRACKER_TEXT.allocation.subtitle}
          titleTag="h6"
          items={allocation}
          dataCy="budget-summary-allocation"
        />
        <FigureTileGroup
          title={TRACKER_TEXT.utilisation.title}
          subtitle={TRACKER_TEXT.utilisation.subtitle}
          titleTag="h6"
          items={utilisation}
          dataCy="budget-summary-utilisation"
        />
      </div>
    </SectionCard>
  );
};

export default Summary;
